<script setup>
import {computed, onMounted} from "vue";
import {useStore} from "vuex";
import RegistrationHero from "../../../../views/login/registration/components/RegistrationHero.vue";
import RegistrationCard from "../../../../views/login/registration/components/RegistrationCard.vue";
import RegistrationIcons from "../../../../views/login/registration/components/RegistrationIcons.vue";
import RegistrationProgress from "../../../../views/login/registration/components/RegistrationProgress.vue";
import CustomerForm from "../../../../views/login/registration/forms/CustomerForm.vue";
import PackageUserStripeForm from "@/components/billing/register/partials/PackageUserStripeForm.vue";
import TermsAndConditions from "@/components/billing/register/partials/TermsAndConditions.vue";

const props = defineProps({
  countries: {
    type: Object,
    required: true,
  },
  userCountry: {
    type: Object,
    required: true,
  },
  packageName: {
    type: String,
    required: true,
  },
  discount: {
    type: [String, null],
    required: false,
    default: null,
  },
  duration: {
    type: String,
    default: 'MONTH',
  },
  availableCurrencies: {
    type: Object,
    required: true,
  },
  availableBillingPeriods: {
    type: Object,
    required: true,
  },
});

const store = useStore();

const stageIsPaymentInformation = computed(() => store.getters['registration/stage'] === 'PAYMENT_INFORMATION');
const currentStage = computed(() => store.getters['registration/stage']);

onMounted(() => {
  let {
    userCountry,
    currency,
    discount,
    countries,
    packageName,
    availableCurrencies,
    availableBillingPeriods,
  } = props;

  if (userCountry.name) {
    store.dispatch('registration/setOperationCountry', {
      value: userCountry.name,
      countries: countries
    });
  }

  if (userCountry.code === 'GB') {
    currency = 'GBP';
  }

  // Ensure currency is valid, if not, fallback.
  if (!['GBP', 'USD', 'gbp', 'usd'].includes(currency)) {
    currency = 'USD';
  }

  // Ensure uppercase, for string comparisons in the back-end
  currency = currency.toUpperCase();

  if (availableCurrencies[currency] === false) {
    for (var currentCurrency in availableCurrencies) {
      if (availableCurrencies[currentCurrency] === true) {
        currency = currentCurrency;
      }
    }
  }

  let billingPeriod = props.duration;
  if (availableBillingPeriods[billingPeriod] === false) {
    for (var currentBillingPeriod in availableBillingPeriods) {
      if (availableBillingPeriods[currentBillingPeriod] === true) {
        billingPeriod = currentBillingPeriod;
      }
    }
  }

  store.dispatch('registration/setConfiguration', {
      userCountry,
      currency,
      billingPeriod,
      discount,
      packageName,
    });

  // If we don't have user country from ip address & the currency is set to DEFAULT USD
  // we should ignore the country that was set in session storage and default it also to 'USA'
  // This way we avoid the currency and country mismatch that can happen
  if (userCountry.name === null && currency === 'USD') {
    store.dispatch('registration/setOperationCountry', {
      value: 'United States',
      countries: countries
    })
  }

  store.dispatch('registration/setLayout', {
    title: 'You’re 1 Min Away From Getting Started',
  });
});
</script>

<template>
  <div class="tw-bg-[#fafafa] tw-font-sans tw-min-h-screen tw-h-full tw-flex tw-flex-col tw-gap-6">
    <RegistrationHero>
      <RegistrationCard>
        <RegistrationProgress :completed="stageIsPaymentInformation" />
        <div
          v-if="stageIsPaymentInformation"
          class="back-to-details"
          @click="store.dispatch('registration/setStage', 'CUSTOMER_INFORMATION')"
        >
          <p class="tw-m-4">
            <i class="arrow" /> back to details
          </p>
        </div>

        <div class="form-container">
          <div class="form">
            <CustomerForm
              v-if="currentStage === 'CUSTOMER_INFORMATION'"
              :countries="countries"
              package-user
            />
            <PackageUserStripeForm
              v-if="stageIsPaymentInformation"
              :available-billing-periods="availableBillingPeriods"
            />
          </div>
          <RegistrationIcons :stage="currentStage" />
        </div>
      </RegistrationCard>
    </RegistrationHero>

    <TermsAndConditions />
  </div>
</template>

<style scoped>
.back-to-details {
  align-self: normal;
  cursor: pointer;
}

.form-container {
  display: flex;
  width: 100%;
  gap: 3rem;
}

.form {
  display: flex;
  flex: 1;
  flex-direction: column;
  background: #FFFFFF;
  gap: 1rem;
}

.form-navigation {
  display: flex;
  width: 100%;
  justify-content: flex-start;
}

.form-navigation p {
  cursor: pointer;
}

.arrow {
  border: solid black;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
  transform: rotate(135deg);
}

@media only screen and (max-width: 900px) {
  .form-container {
    flex-direction: column;
  }
}
</style>
